"use client"

/**
 * Finance governance (ADR-0024) — how this organization runs its books.
 *
 * The step is deliberately a fork in the road rather than a toggle: the two answers produce genuinely
 * different products, the choice cannot be taken back through the UI, and it decides whether the accounts the
 * later steps create are global or branch-owned. So it states both consequences plainly and says on screen
 * that it settles when the parish finishes setup.
 */

import * as React from "react"

import { BuildingIcon, CheckIcon, EyeOffIcon, LandmarkIcon, LockIcon } from "lucide-react"

import { HelperMessage, cn } from "@ibada/ui"
import { useIsMobile } from "@/shared/hooks/use-mobile"

import { useTranslations } from "@/i18n"
import { FinanceGovernance } from "@/features/finance/types/finance-governance"
import {
  useFinanceGovernanceImpactQuery,
  useSetFinanceGovernanceMutation,
} from "@/features/finance/services/finance-setup-api"

/** The wizard's Continue saves this step, the way it already does for the accounting identity. */
export type GovernanceStepHandle = { save: () => Promise<boolean> }

const OPTIONS = [
  { value: FinanceGovernance.FullParish, key: "fullParish", Icon: LandmarkIcon },
  { value: FinanceGovernance.BranchAutonomy, key: "branchAutonomy", Icon: BuildingIcon },
] as const

export const OnboardingGovernanceStep = React.forwardRef<
  GovernanceStepHandle,
  {
    workspaceId: string
    branchId: string
    canEdit: boolean
    /** What the server currently holds — the default until the treasurer says otherwise. */
    value: FinanceGovernance
    /**
     * Has this question already been answered in an earlier sitting?
     *
     * The server always holds a governance value, so `value` alone cannot tell a DEFAULT apart from a CHOICE.
     * The wizard knows, because progress was stored past this step.
     */
    alreadyAnswered: boolean
    onChosenChange: (chosen: boolean) => void
  }
>(function OnboardingGovernanceStep(
  { workspaceId, branchId, canEdit, value, alreadyAnswered, onChosenChange },
  ref,
) {
  const t = useTranslations("finance.onboarding.governance")
  const setGovernance = useSetFinanceGovernanceMutation(workspaceId, branchId)

  /*
   * Unset on a FIRST visit — a choice this permanent should be made, not inherited by clicking past a
   * pre-ticked default. But on a RETURN the answer already exists and belongs to the treasurer, so showing
   * nothing selected told them their decision had been thrown away. It had not; it was simply never read
   * back, because the server's value cannot be told apart from its default without knowing they had been
   * here before.
   */
  const [selected, setSelected] = React.useState<FinanceGovernance | null>(alreadyAnswered ? value : null)

  /*
   * Seed from the stored answer when it ARRIVES, not only when this mounts.
   *
   * A `useState` initializer captures its argument once. This step can mount before the setup status has
   * said whether the question was ever answered, and the initializer never runs again — so the props were
   * correct a moment later while the screen went on showing nothing selected, which is exactly what read as
   * "my choice was thrown away".
   *
   * Only ever fills a BLANK selection: whatever the reader is pointing at now outranks what the server holds.
   */
  React.useEffect(() => {
    if (!alreadyAnswered) return
    setSelected((current) => current ?? value)
  }, [alreadyAnswered, value])

  // Fetched only once autonomy is the option in hand — it costs a query, and the full-parish path withdraws
  // nothing, so there is nothing to warn about there.
  const impact = useFinanceGovernanceImpactQuery(
    workspaceId,
    selected === FinanceGovernance.BranchAutonomy,
  )

  React.useEffect(() => {
    onChosenChange(selected !== null)
  }, [selected, onChosenChange])

  /**
   * Persist the choice.
   *
   * `alreadyAnswered` decides whether the server holds an ANSWER or merely its default, so a selection equal
   * to `value` still has to be written the first time — that write is what turns the default into a decision.
   * Skipping it (the old `selected === value` shortcut) meant picking "Full parish", the default, saved
   * nothing at all.
   */
  const persist = React.useCallback(
    async (choice: FinanceGovernance) => {
      if (alreadyAnswered && choice === value) return true
      try {
        await setGovernance.mutateAsync(choice)
        return true
      } catch {
        return false // the shared API error toast already explained why
      }
    },
    [alreadyAnswered, value, setGovernance],
  )

  /*
   * Saved on selection, not on Continue.
   *
   * Continue was the only thing that wrote, so choosing an option and then pressing Back — or Save for later,
   * or simply navigating away — discarded it silently. The reader had made a decision and the screen showed
   * it; nothing said it was being held hostage by a button they had not pressed yet.
   */
  const isMobile = useIsMobile()
  const choose = (option: FinanceGovernance) => {
    setSelected(option)
    void persist(option)
  }

  React.useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        if (!selected) return false
        return persist(selected)
      },
    }),
    [selected, persist],
  )

  return (
    <div className={isMobile ? "flex flex-col gap-4" : "space-y-5"}>
      {isMobile ? (
        // Phone: the same warning as a soft note, not a bordered banner.
        <div className='flex items-start gap-3 rounded-2xl bg-amber-500/10 px-4 py-3 text-amber-900 dark:text-amber-200'>
          <LockIcon className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
          <p className='text-[13px] font-semibold'>{t("permanence")}</p>
        </div>
      ) : (
      <HelperMessage tone='warning' size='md' icon={LockIcon}>
        {t("permanence")}
      </HelperMessage>
      )}

      <div
        className='grid gap-3 sm:grid-cols-2'
        role='radiogroup'
        aria-label={t("legend")}
        aria-required='true'
      >
        {OPTIONS.map(({ value: option, key, Icon }) => {
          const active = selected === option
          return (
            <button
              key={option}
              type='button'
              role='radio'
              aria-checked={active}
              disabled={!canEdit || setGovernance.isPending}
              onClick={() => choose(option)}
              className={
                isMobile
                  ? // Phone: a white choice card; the chosen one gets the green ring and check.
                    cn(
                      "relative flex flex-col gap-1.5 rounded-2xl bg-card p-4 text-left transition-shadow disabled:opacity-60",
                      active ? "ring-2 ring-primary" : "ring-1 ring-border/60",
                    )
                  : `focus-visible:ring-ring/50 relative flex h-full flex-col gap-2 rounded-xl border p-4 text-left transition-colors focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
                      active ? "border-primary bg-primary/5" : "hover:border-muted-foreground/30 border-border"
                    }`
              }
            >
              <span className='flex items-center gap-2'>
                <Icon className={cn("size-4 shrink-0", isMobile && active ? "text-primary" : "text-muted-foreground")} aria-hidden='true' />
                <span className={isMobile ? "text-base font-extrabold" : "font-medium"}>{t(`${key}.title`)}</span>
                {active ? <CheckIcon className='text-primary ml-auto size-4' aria-hidden='true' /> : null}
              </span>
              <span className='text-muted-foreground text-sm leading-relaxed'>{t(`${key}.body`)}</span>
              <span className='text-muted-foreground/80 mt-auto pt-1 text-xs leading-relaxed'>
                {t(`${key}.parishSees`)}
              </span>
            </button>
          )
        })}
      </div>

      {/* Autonomy WITHDRAWS the parish side's finance grants on these branches (ADR-0024 §2) — provisioning
          handed them out at creation, so without this the mode would change nothing for the owner. Named
          rather than described, because "you will lose some access" is not a thing anyone can consent to. */}
      {selected === FinanceGovernance.BranchAutonomy && impact.data?.branches.length ? (
        <HelperMessage tone='warning' size='md' icon={EyeOffIcon}>
          <span className='block'>{t("revocation.title")}</span>
          <span className='text-muted-foreground mt-1 block text-xs'>
            {impact.data.branches.map((branch) => branch.name).join(" · ")}
          </span>
          <span className='mt-1 block text-xs'>{t("revocation.body")}</span>
        </HelperMessage>
      ) : null}
    </div>
  )
})
